import axios from 'axios';
import logger from '@/utils/logger';
import stringify from '@/utils/stringify';
import env from '@/utils/env';
import store from '@/store';

/**
 * 钱包模块接口
 */
class MerchantService {
    /**
     * 构造函数
     */
    constructor() {
        // 是否调用mock接口
        this.isMock = false;
        this.mockPrefix = 'mockPrefix';
        this.serverPrefix = 'serverPrefix';

        // 初始化axios实例
        this.axios = axios.create({
            baseURL: '/',
            headers: {
                'Content-Type': 'application/json'
            },
            paramsSerializer: params => stringify(params),
            validateStatus: status => status >= 200 && status < 300
        });

        // 安装拦截器
        this.setupRequestInterceptors();
        this.setupResponseInterceptors();
    }

    /**
     * 安装请求拦截器
     */
    setupRequestInterceptors() {
        // 请求拦截器
        this.axios.interceptors.request.use(
            config => {
                if (store.state.user.token) {
                    // 给每次请求带上token
                    config.headers.Authorization = `Bearer ${store.state.user.token}`;
                }

                if (env.VUE_APP_LOCAL) {
                    // 反向代理前缀
                    const proxyPrefix = this.isMock ? this.mockPrefix : this.serverPrefix;
                    // 添加反向代理前缀
                    config.url = `${proxyPrefix}/${config.url}`;
                }
                // 如果接口前缀带api/pay/ 则替换为环境变量VUE_APP_NG_SERVER_URL
                config.url = config.url.replace('api/pay/', process.env.VUE_APP_NG_SERVER_URL);

                // 给请求添加时间戳
                config.params = config.params || {};
                config.params.request_no = new Date().getTime();

                // 添加票据参数
                if (store.state.user.payToken) {
                    // config.params.ticket = store.state.user.payToken;
                    config.headers.ticket = store.state.user.payToken;
                }

                return config;
            },
            error => {
                logger.error(error);
                return Promise.reject(error);
            }
        );
    }

    /**
     * 安装响应拦截器
     */
    setupResponseInterceptors() {
        // 响应拦截器
        this.axios.interceptors.response.use(
            response => {
                return response;
            },
            error => {
                const defaultError = error.response || {
                    message: '网络超时',
                    code: 500
                };
                // 身份验证失败
                let isValidateFail = false;

                // 身份验证失败后是否需要跳转
                let redirectWhenFail = false;

                if (error.response && error.response.data) {
                    defaultError.code = error.response.data.code;
                    defaultError.message = error.response.data.message;

                    switch (parseInt(error.response.data.code || 200, 10)) {
                        case 40002:
                            defaultError.message = '没有权限访问，请联系管理员';
                            isValidateFail = true;
                            redirectWhenFail = true;
                            break;
                        case 40003:
                            // 优先判断是否被踢出
                            defaultError.message = '用户被停用，请联系管理员';
                            isValidateFail = true;
                            redirectWhenFail = true;
                            break;
                        case 40004:
                            // 优先判断是否被踢出
                            defaultError.message = '登录过期，请重新登录';
                            isValidateFail = true;
                            redirectWhenFail = true;
                            break;
                        case 40006:
                            // 优先判断是否被踢出
                            defaultError.message = '账号认证失败，请联系管理员';
                            isValidateFail = true;
                            redirectWhenFail = true;
                            break;
                        case 44129:
                            defaultError.message = '当前登录已失效';
                            isValidateFail = true;
                            redirectWhenFail = true;
                            break;
                        case 101012:
                            // 优先判断是否被踢出
                            defaultError.message = '无权访问，请联系管理员';
                            isValidateFail = false;
                            redirectWhenFail = false;
                            break;
                        case 101021:
                            // 优先判断是否被踢出
                            defaultError.message = '用户名或密码错误';
                            isValidateFail = true;
                            redirectWhenFail = true;
                            break;
                        case 101022:
                            // 优先判断是否被踢出
                            defaultError.message = '当前登录已失效，请重新登录';
                            isValidateFail = true;
                            redirectWhenFail = true;
                            break;
                        default:
                            break;
                    }

                    if (isValidateFail && redirectWhenFail) {
                        // 身份存在问题并且需要跳转到登录页面
                    }
                }

                return Promise.reject(defaultError);
            }
        );
    }
    /**
     * 状态字典码
     */
    getDictList(codeType) {
        return this.axios.get(`api/pay-center/v1/common/dict/list?codeType=${codeType}`);
    }
    /**
     * 查询基本账户统计
     */
    getAccountStatistics(params) {
        return this.axios.get('api/pay-center/v1/counter/merchant/account-amount', { params });
    }
    /**
     * 查询基本账户列表
     */
    getAccountList(params) {
        return this.axios.get('api/pay-center/v1/counter/merchant/bill', {
            params
        });
    }
    /**
     * 获取账户账单详情
     */
    getBaseAccountDetail(params) {
        return this.axios.get(`api/pay-center/v1/counter/merchant/bill-detail-busiSn`, { params });
    }
    /**
     * 消费账单
     */
    getConsumeTrade(params) {
        return this.axios.get('api/pay-center/v1/counter/merchant/consume-trade', { params });
    }
    /**
     * 消费统计
     */
    getConsumeAmount() {
        return this.axios.get('api/pay-center/v1/counter/merchant/consume-amount');
    }
    /**
     * 退款账单
     */
    getRefundTrade(params) {
        return this.axios.get('api/pay-center/v1/counter/merchant/refund-trade', { params });
    }
    /**
     * 退款统计
     */
    getRefundAmount() {
        return this.axios.get('api/pay-center/v1/counter/merchant/refund-amount');
    }
    /**
     * 结算统计
     */
    getSettleAmount() {
        return this.axios.get('api/pay-center/v1/counter/merchant/settle-amount');
    }
    /**
     * 结算详情统计
     */
    getSettleDetailAmount(params) {
        return this.axios.get('api/pay-center/v1/counter/merchant/settle-detail', { params });
    }
    /**
     * 结算详情列表
     */
    getSettleDetailist(params) {
        return this.axios.get('api/pay-center/v1/counter/merchant/settle-items', { params });
    }
    /**
     * 结算账单
     */
    getSettleTrade(params) {
        return this.axios.get('api/pay-center/v1/counter/merchant/settle-trade', { params });
    }
    /**
     * 产品中心状态统计
     */
    getChannelStatic() {
        return this.axios.get('api/pay-center/v1/counter/merchant/channel/static');
    }
    /**
     * 授信交易统计
     */
    getTradeStatic() {
        return this.axios.get('api/pay-center/v1/counter/credit/merchant/trade-statistics');
    }
    /**
     * 授信交易列表
     */
    getTradeList(params) {
        return this.axios.get('api/pay-center/v1/counter/credit/merchant/trade-list', { params });
    }
    /**
     * 授信交易详情
     */
    getTradeDetail(params) {
        return this.axios.get('api/pay-center/v1/counter/credit/merchant/trade-detail', { params });
    }
    /**
     * 授信额度统计信息
     */
    getMerchantStatistics() {
        return this.axios.get('api/pay-center/v1/counter/credit/merchant/statistics');
    }
    /**
     * 授信额度列表
     */
    getMerchantList(params) {
        return this.axios.get('api/pay-center/v1/counter/credit/merchant/list', { params });
    }
    /**
     * 授信所有状态
     */
    getMerchantStatusList(params) {
        return this.axios.get('api/pay-center/v1/counter/credit/merchant/status-list', { params });
    }
    /**
     * 授信额度详情
     */
    getCreditDetail(params) {
        return this.axios.get('api/pay-center/v1/counter/credit/merchant/detail', { params });
    }
    /**
     * 授信变更记录
     */
    getCreditChangeLog(params) {
        return this.axios.get('api/pay-center/v1/counter/credit/merchant/change-log-list', { params });
    }
    /**
     * 授信月度账单统计信息
     */
    getCreditMonthBill(params) {
        return this.axios.get('api/pay-center/v1/counter/credit/merchant/month-bill', { params });
    }
    /**
     * 授信月度账单列表
     */
    getCreditMonthTradeList(params) {
        return this.axios.get('api/pay-center/v1/counter/credit/merchant/month-trade-list', { params });
    }
    /**
     * 授信冻结
     */
    getCreditFrozen(params) {
        return this.axios.put('api/pay-center/v1/counter/credit/merchant/frozen', params);
    }
    /**
     * 授信解冻
     */
    getCreditUnfrozen(params) {
        return this.axios.put('api/pay-center/v1/counter/credit/merchant/unfrozen', params);
    }
    /**
     * 授信停用
     */
    getCreditClose(params) {
        return this.axios.put('api/pay-center/v1/counter/credit/merchant/close', params);
    }
    /**
     * 授信额度修改
     */
    getCreditOverdraft(params) {
        return this.axios.put('api/pay-center/v1/counter/credit/merchant/overdraft', params);
    }
    /**
     * 查询钱包列表
     */
    getCreditWallets(params) {
        return this.axios.get('api/pay-center/v1/counter/merchant/corporate/wallets', { params });
    }
    /**
     * 开通授信
     */
    getCreditCreate(params) {
        return this.axios.post('api/pay-center/v1/counter/credit/merchant/create', params);
    }
    /**
     * 重新开通授信
     */
    getCreditReopen(params) {
        return this.axios.put('api/pay-center/v1/counter/credit/merchant/reopen', params);
    }
    /**
     * 产品中心支付渠道
     */
    getMerchantChannel(params) {
        return this.axios.get('api/pay-center/v1/counter/merchant/channel', { params });
    }
    /**
     * 订单子账户统计
     */
    getSubAccountStatistics() {
        return this.axios.get('api/pay-center/v1/counter/merchant/order-account-statistics');
    }
    /**
     * 订单子账户列表
     */
    getSubAccountList(params) {
        return this.axios.get('api/pay-center/v1/counter/merchant/order-accounts',{params});
    }
    /**
     * 订单子账户详情
     */
    getSubAccountDetail(params) {
        return this.axios.get('api/pay-center/v1/counter/merchant/order-account-detail',{params});
    }
    /**
     * 订单子账户详情页面的订单详情
     */
    getNowOrder(params) {
        return this.axios.get('api/pay-center/v1/counter/merchant/order-account/order-detail',{params});
    }
    /**
     * 订单子账户历史订单列表--分页
     */
    getHistoryList(params) {
        return this.axios.get('api/pay-center/v1/counter/merchant/account-history-orders',{params});
    }
     /**
     * 订单子账户交易记录--分页
     */
     getTransactionsList(params) {
        return this.axios.get('api/pay-center/v1/counter/merchant/order-account/trades',{params});
    }
    /**
     * 订单子账户交易记录--分页
     */
    getOpersList(params) {
        return this.axios.get('api/pay-center/v1/counter/merchant/order-account/oper-records',{params});
    }
    /**
     * 订单管理统计
     */
    getBizOrderStatistics() {
        return this.axios.get('api/pay-center/v1/counter/merchant/biz-order-statistics');
    }
    /**
     * 订单管理列表
     */
    getBizOrderList(params) {
        return this.axios.get('api/pay-center/v1/counter/merchant/biz-orders',{params});
    }
    /**
     * 订单管理详情
     */
    getBizOrderDetail(params) {
        return this.axios.get('api/pay-center/v1/counter/merchant//biz-order-detail',{params});
    }
     /**
     * 订单管理-款项列表
     */
     getBizOrderPayments(params) {
        return this.axios.get('api/pay-center/v1/counter/merchant/biz-order-payments',{params});
    }
    /**
     * 订单子账户交易类型列表
     */
    getBizTypes(params) {
        return this.axios.get('api/pay-center/v1/counter/merchant/order-account/biz-types',{params});
    }
    /**
     * 商户--安全设置--查询商户详情
     */
    getCounterDetail() {
        return this.axios.get('api/pay-center/v1/counter/merchant/detail');
    }

    /**
     * 获取票据
     */
    getTicket() {
        return this.axios.get('sanyapi/sany/v1/walletAuth/getTicket');
    }

    /**
     * 获取用户名
     */
    getUserName() {
        return this.axios.get('api/pay/v1/publicFunction/userName');
    }

    /**
     * 获取运营账户统计数据
     */
    getTotalForMerchant(params) {
        return this.axios.get('api/pay/v1/merchant/operation/total', {
            params
        });
    }

    /**
     * 获取钱包统计数据
     */
    getTotalForWallet(params) {
        return this.axios.get('api/pay/v1/merchant/wallet/total', {
            params
        });
    }

    /**
     * 获取运营账户账单列表
     */
    getListForMerchant(params) {
        return this.axios.get('api/pay/v1/merchant/operation/trade', {
            params
        });
    }

    /**
     * 获取钱包账单列表
     */
    getListForWallet(params) {
        return this.axios.get('api/pay/v1/merchant/wallet/trades', {
            params
        });
    }

    /**
     * 获取运营账户业务类型
     */
    getListForMerchantBizType() {
        return this.axios.get('api/pay/v1/tradeDetail/listForMerchantBizType');
    }
    /**
     * 获取商户列表
     */
    getMerchantsList(params) {
        return this.axios.get('api/pay/v1/brief-merchants', { params });
    }

    /**
     * 获取运营账户账单详情
     */
    getDetailForMerchant(params) {
        // return this.axios.get(`api/pay/v1/merchant/operation/trade/${params.id}`, {
        //     params
        // });
        return this.axios.get(`api/pay/v1/merchant/operation/trade/detail`, {
            params
        });
    }

    /**
     * 查询交易凭证，三湘银行的
     */
    queryTradeCertificate(params) {
        return this.axios.get(`api/pay/v1/merchant/operation/trade/certificate/${params.busiSn}`, {
            params
        });
    }

    /**
     * 发送验证码
     */
    sendCode(params) {
        return this.axios.get('api/pay/v1/function/sendSMS', {
            params
        });
    }
    /**
     * 查询基本信息
     */
    baseAccountInfo(params) {
        return this.axios.get('api/pay-center/v1/counter/merchant/acct-info', {
            params
        });
    }
    /**
     * 提现发送验证码
     */
    withdrawSendCode(params) {
        return this.axios.post('api/pay-center/v1/counter/pay/mer-send-withdraw-sms',params);
    }
    /**
     * 商户提现
     */
    baseAccountOut(params) {
        return this.axios.post('api/pay-center/v1/counter/merchant/async/withdraw',params);
    }

    /**
     * 查询本地账户金额，基本账户详情
     */
    searchMerchantInfo(params) {
        return this.axios.get('api/pay/v1/merchant/searchMerchantInfo', {
            params
        });
    }

    /**
     * 查询钱包详情
     */
    searchWalletInfo(params) {
        return this.axios.get('api/pay/v1/merchant/wallet/info', {
            params
        });
    }

    /**
     * 获取返佣统计
     */
    getTotalCommission() {
        return this.axios.get('api/pay/v1/commission/totalCommission');
    }

    /**
     * 获取返佣列表
     */
    getCommissionList(params) {
        return this.axios.get('api/pay/v1/commission/page', {
            params
        });
    }

    /**
     * 获取返佣统计数据
     */
    getCommissionStatisticsByStatus(params) {
        return this.axios.get('api/pay/v1/commission/statisticsByStatus', {
            params
        });
    }

    /**
     * 返佣发放
     */
    getCommissionPay(params) {
        return this.axios.get('api/pay/v1/commission/pay', {
            params
        });
    }

    /**
     * 取消返佣发放
     */
    getCommissionPayCancel(params) {
        return this.axios.get('api/pay/v1/commission/cancel', {
            params
        });
    }

    /**
     * 获取返佣详情
     */
    getCommissionDetail(params) {
        return this.axios.get('api/pay/v1/commission/detail', {
            params
        });
    }

    /**
     * 导出返佣
     */
    exportCommission() {
        return this.axios.get('api/pay/v1/commission/export');
    }

    /**
     * 获取返佣配置
     */
    getCommissionConfig() {
        return this.axios.get('api/pay/v1/commission/config');
    }

    /**
     * 修改返佣配置
     */
    updateCommissionConfig(params) {
        return this.axios.put('api/pay/v1/commission/config', params);
    }

    /**
     * 申请成为开发者
     */
    applyDeveloper(params) {
        return this.axios.post('api/pay/v1/merchant/developer', params);
    }

    /**
     * 查看开发者
     */
    searchDeveloper() {
        return this.axios.get('api/pay/v1/merchant/developer');
    }

    /**
     * 重置app_secret
     */
    resetAppSecret() {
        return this.axios.post('api/pay/v1/merchant/app/secret');
    }

    /**
     * 更新url回调地址和ip白名单
     */
    updateUrlAndIp(params) {
        return this.axios.put('api/pay/v1/merchant/app/urlAndIp', params);
    }

    /**
     * 获取退款列表
     */
    getRefundList(params) {
        return this.axios.get('api/pay/v1/merchant/refund', {
            params
        });
    }

    /**
     * 获取交易订单列表
     */
    getTransactionList(params) {
        return this.axios.get('api/pay/v1/merchant/transaction', {
            params
        });
    }

    /**
     * 获取退款统计
     */
    getRefundStatement() {
        return this.axios.get('api/pay/v1/merchant/refund/statement');
    }

    /**
     * 获取交易统计
     */
    getTransactionStatement() {
        return this.axios.get('api/pay/v1/merchant/transaction/statement');
    }

    /**
     * 获取运营账户是否开通
     */
    getOperationStatus() {
        return this.axios.get('api/pay/v1/merchant/operation/getMerchantStatus');
    }
    /**
     * 获取基本账户是否开通
     */
    getBasicStatus() {
        return this.axios.get('api/pay/v1/merchant/basic/getMerchantStatus');
    }
    /**
     * 获取基本账户结算记录
     */
    queryBasicSettlements(params) {
        return this.axios.get('api/pay/v1/merchant/basic/settlements', { params });
    }
    /**
     * 获取基本账户结算记录
     */
    queryBasicSettlementDetails(settleId) {
        return this.axios.get(`api/pay/v1/merchant/basic/settlement/details/${settleId}`);
    }
    /**
     * 基本账户-开户信息查询
     */
    searchOpenBasicInfo() {
        return this.axios.get('api/pay/v1/merchant/basic/searchOpenMerchantInfo');
    }
    /**
     * 运营账户-开户信息查询
     */
    searchOpenOperationInfo() {
        return this.axios.get('api/pay/v1/merchant/operation/searchOpenMerchantInfo');
    }
    /**
     * 查询银行行名行号
     */
    queryBank(params) {
        return this.axios.get('api/pay/v1/function/queryBank', {
            params
        });
    }
    /**
     * 获取开户流水号
     */
    getTranSeqNum() {
        return this.axios.get('api/pay/v1/function/getTranSeqNum');
    }
    /**
     * 获取短信验证码
     */
    sendSMS(params) {
        return this.axios.get('api/pay/v1/function/sendSMS', { params });
    }
    /**
     * 基本账户-开户
     */
    openBasictAccount(params) {
        return this.axios.post('api/pay/v1/merchant/basic/openAccount', params);
    }
    /**
     * 运营账户-开户
     */
    openOperationAccount(params) {
        return this.axios.post('api/pay/v1/merchant/operation/openAccount', params);
    }
    /**
     * 获取基本账户是否开通
     */
    getInfo() {
        return this.axios.get('api/pay/v1/user/info');
    }

    /**
     * 查询商户联系人信息
     */
    getContactInfo() {
        return this.axios.get('api/pay/v1/merchant/contact');
    }
    /**
     * 修改商户手机号
     */
    putTelephone(params) {
        return this.axios.put('api/pay/v1/merchant/telephone', params);
    }
    /**
     * 查询融资账户详情
     */
    getExhibitionInfo(params) {
        return this.axios.get('api/pay/v1/merchant/exhibition/info', {
            params
        });
    }
    /**
     * 获取融资账单列表
     */
    getListExhibition(params) {
        return this.axios.get('api/pay/v1/merchant/exhibition/trade', {
            params
        });
    }
    // 开票管理
    /**
     * 获取开票管理统计
     */
    getInvoiceStat() {
        return this.axios.get('api/pay/v1/invoice/statistics');
    }
    // 获取开票管理列表
    queryInvoicedSettlements(params) {
        return this.axios.get('api/pay/v1/invoice', {
            params
        });
    }
    // 获取未开票列表
    getUnbilledMonth() {
        return this.axios.get('api/pay/v1/invoice/unbilled');
    }
    // 获取未开票详情
    queryCloseInvoiceDetails(params) {
        return this.axios.get('api/pay/v1/invoice/unbilled/detail', {
            params
        });
    }
    // 申请开票
    postApplyInvoice(params) {
        return this.axios.post('api/pay/v1/invoice/apply', params);
    }
    // 获取发票详情
    getInvoiceDetail(id) {
        return this.axios.get('api/pay/v1/invoice/' + id);
    }
    // 发票申请提交
    postApplySubmit(params) {
        return this.axios.post('api/pay/v1/invoice', params);
    }
    // 发票月份账单详情
    queryInvoicedBillGroupDetails(params) {
        return this.axios.get('api/pay/v1/invoice/bill/detail', { params });
    }

    // 在外货款总览
    queryLoansAbroadOverView(params) {
        return this.axios.get('api/pay-product-dms/v1/shop/installment/overview', { params });
    }
    // 在外货款列表
    queryLoadnsAbroadSettlements(params) {
        return this.axios.get('api/pay-product-dms/v1/shop/installment/list', { params });
    }
    // 在外货款详情
    getLoanAbroadDetail(id) {
        return this.axios.get('api/pay-product-dms/v1/shop/installment/getInstallmentDetail/' + id);
    }
    // 申请结清
    getApplySettlement(id) {
        return this.axios.get('api/pay-product-dms/v1/shop/settlement/' + id);
    }
    // 催缴货款
    getCallPayment(id) {
        return this.axios.get('api/pay-product-dms/v1/shop/installment/remindRepayment/' + id);
    }
    // 结清申请列表
    querySettleSettlements(params) {
        return this.axios.get('api/pay-product-dms/v1/shop/settlement/list', { params });
    }
    // 逾期罚息总览
    queryLateChargeOverView(params) {
        return this.axios.get('api/pay-product-dms/v1/shop/interest/overview', { params });
    }
    // 逾期罚息列表
    queryLateChargeSettlements(params) {
        return this.axios.get('api/pay-product-dms/v1/shop/interest/list', { params });
    }
    // 坏账核销总览
    querybadDebtOverView(params) {
        return this.axios.get('api/pay-product-dms/v1/shop/writeoff/overview', { params });
    }
    // 坏账核销列表
    querybadDebtSettlements(params) {
        return this.axios.get('api/pay-product-dms/v1/shop/writeoff/list', { params });
    }
    // 坏账核销详情
    getBadDebtDetail(params) {
        return this.axios.get('api/pay-product-dms/v1/shop/writeOff/getWriteOffDetail', { params });
    }
    // 坏账核销保存
    postBadDebtSave(params) {
        return this.axios.post('api/pay-product-dms/v1/shop/writeOff/save', params);
    }
    // 坏账核销提交
    postBadDebtSubmit(params) {
        return this.axios.post('api/pay-product-dms/v1/shop/writeOff/submit', params);
    }
    // 坏账核销通过
    postBadDebtPass(params) {
        return this.axios.get('api/pay-product-dms/v1/shop/writeOff/pass', { params });
    }
    // 坏账核销驳回
    postBadDebtReject(params) {
        return this.axios.get('api/pay-product-dms/v1/shop/writeOff/reject', { params });
    }
    // 坏账核销申请创建
    getWriteOffSettlement(id) {
        return this.axios.get(`api/pay-product-dms/v1/shop/writeOff/${id}`);
    }
    getFileUrlDownLoad(params) {
        return this.axios.get(`api/pay/v1/getFileUrl`, { params });
    }
    getOfflineConfigList(params) {
        return this.axios.get(`api/pay-center/v1/counter/merchant/offline-account`, { params });
    }
    modifyOfflineConfig(params) {
        return this.axios.put(`api/pay-center/v1/counter/merchant/offline-account`, params);
    }
    addOfflineConfig(params) {
        return this.axios.post(`api/pay-center/v1/counter/merchant/offline-account`, params);
    }
    disableOfflineConfig(id) {
        return this.axios.put(`api/pay-center/v1/counter/merchant/offline-account/disable/${id}`);
    }
    enableOfflineConfig(id) {
        return this.axios.put(`api/pay-center/v1/counter/merchant/offline-account/enable/${id}`);
    }
    getOfflineStatic(params) {
        return this.axios.get(`api/pay-center/v1/counter/ntis-transaction/history-statistics`, { params });
    }
    getOfflineList(params) {
        return this.axios.get(`api/pay-center/v1/counter/ntis-transaction/history`, { params });
    }
    getOfflineType() {
        return this.axios.get(`api/pay-center/v1/counter/ntis-transaction/settlement-type`);
    }
    getOfflineStatus() {
        return this.axios.get(`api/pay-center/v1/counter/ntis-transaction/accounting-status`);
    }
    getOfflineSync() {
        return this.axios.get(`api/pay-center/v1/counter/ntis-transaction/history-sync`);
    }
    getOfflineVoucher() {
        return this.axios.get(`api/pay-center/v1/counter/ntis-transaction/voucher-sync`);
    }
    getOfflineOrder(params) {
        return this.axios.get(`api/pay-center/v1/counter/ntis-transaction/paid-order`,{params});
    }
    // 同步支付流水信息
    syncInformation(params) {
        return this.axios.post('api/pay-center/v1/counter/ntis-transaction/sap-sync-manual',params);
    }
    // 最近同步时间
    syncLatestInfo() {
        return this.axios.get('api/pay-center/v1/counter/ntis-transaction/query-sync-info');
    }
}

const merchantService = new MerchantService();
export default merchantService;
